import React, { useEffect, useState } from "react";
import { Button, Checkbox, Form, FormInstance, Input, Select } from "antd";
import { DeleteFilled, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { AddAnotherConditionalStyledButton, AddConditionalStyledButton, AddFieldWrapper, StyledConditionalContainer, StyledConditionalRemoveButton, StyledOptionContainer, StyledOptionWrapper } from "./styles";
import { SubQuestion, SubQuestionTypes } from "../../types/QuestionTypes";

type DynamicOptionFormProps = {
  form: FormInstance<any>;
}

const DynamicOptionForm = ({ form }: DynamicOptionFormProps) => {
  const [optionData, setOptionData] = useState<
    { id: string | number; name: string; subQuestion: SubQuestion[] }[]
  >([{ id: 1, name: "", subQuestion: [{ id: 1, fieldName: "", fieldType: 1, isRequired: false }] }]);
  const [toggledOptions, setToggledOptions] = useState<any>({});
  const [clearSubQuestion, setClearSubQuestion] = useState<boolean>(true);

  const handleToggleOption = (optionId: string | number) => {
    setClearSubQuestion(false);
    setToggledOptions((prev: any) => {
      const isToggled = !prev[optionId];
      setOptionData((prevOptions) => prevOptions.map((option) => (option.id === optionId ? { ...option, subQuestion: isToggled ? option.subQuestion : [{ id: 1, fieldName: "", fieldType: 1, isRequired: false }] } : option)));
      return { ...prev, [optionId]: isToggled };
    });
  };

  const handleChange = (optionId: string | number, questionId: string | number | null, field: keyof SubQuestion, value: string | boolean | number) => {
    setOptionData((prevOptions) =>
      prevOptions.map((option) => {
        if (option.id === optionId) {
          if (questionId) {
            return {
              ...option,
              subQuestion: option.subQuestion.map((q) => (q.id === questionId ? { ...q, [field]: value } : q)),
            };
          }

          // If the input field is cleared, set toggledOptions to false for this optionId
          if (field === "name" && value === "") {
            if (toggledOptions[option?.id]) {
              setToggledOptions((prev: any) => ({ ...prev, [optionId]: false }));
            }
          }

          return { ...option, [field]: value };
        }
        return option;
      })
    );
  };

  const addOption = () => {
    setOptionData((prevOptions) => [...prevOptions, { id: prevOptions.length + 1, name: "", subQuestion: [{ id: 1, fieldName: "", fieldType: 1, isRequired: false }] }]);
  };

  const removeOption = (optionId: string | number) => {
    setOptionData(optionData.filter((opt) => opt.id !== optionId));
  };

  const addQuestion = (optionId: string | number) => {
    setOptionData((prevOptions) =>
      prevOptions.map((opt) =>
        opt.id === optionId
          ? {
            ...opt,
            subQuestion: [...opt.subQuestion, { id: opt.subQuestion.length + 1, fieldName: "", fieldType: 1, isRequired: false }],
          }
          : opt
      )
    );
  };

  const removeQuestion = (optionId: string | number, questionId: string | number) => {
    setOptionData(optionData.map((opt) => (opt.id === optionId ? { ...opt, subQuestion: opt.subQuestion.filter((q) => q.id !== questionId) } : opt)));
  };

  useEffect(() => {
    if (form.getFieldValue('id')) {
      const currentOptions = form.getFieldValue("options");
      if (currentOptions && Array.isArray(currentOptions) && clearSubQuestion) {
        setOptionData(currentOptions);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getFieldValue('id'), form, toggledOptions]);

  useEffect(() => {
    form.setFieldValue("options", optionData);
  }, [form, optionData]);

  return (
    <Form.Item name="options">
      <span>Options:</span>
      {optionData?.map((option) => (
        <StyledOptionContainer key={option.id} style={{ marginBottom: "10px" }}>
          <StyledOptionWrapper>
            <div>
              <Input style={{ width: "100%" }} name={`Option ${option?.id}`} size="large" placeholder={`Option ${option?.id}`} value={option.name} onChange={(e) => handleChange(option.id, null, "name", e.target.value)} />
            </div>
            <div>
              <AddConditionalStyledButton disabled={!option.name.trim()} type="primary" onClick={() => handleToggleOption(option?.id)}>
                {toggledOptions[option?.id] ? (
                  <>
                    <MinusOutlined /> Remove Conditional Question
                  </>
                ) : (
                  <>
                    <PlusOutlined /> Add Conditional Question
                  </>
                )}
              </AddConditionalStyledButton>
            </div>
            <div>
              <Button icon={<DeleteFilled />} onClick={() => removeOption(option.id)} />
            </div>
          </StyledOptionWrapper>

          {toggledOptions[option?.id] && (
            <div style={{ width: "100%", padding: "0px 0px" }}>
              {option?.subQuestion?.map((question, idx) => (
                <StyledConditionalContainer key={question.id} style={{ marginBottom: "10px" }}>
                  <AddFieldWrapper>
                    <div>#{idx + 1}</div>
                    <StyledConditionalRemoveButton onClick={() => removeQuestion(option.id, question.id)}>
                      <MinusOutlined /> Remove
                    </StyledConditionalRemoveButton>
                  </AddFieldWrapper>

                  <Form.Item style={{ width: "100%" }} initialValue={question.fieldName} name={`fieldName_${option.id}_${question.id}`} label="Name" rules={[{ required: true, message: "Field name is required" }]}>
                    <Input size="large" placeholder="Field name" value={question.fieldName} onChange={(e) => handleChange(option.id, question.id, "fieldName", e.target.value)} />
                  </Form.Item>

                  <Form.Item style={{ width: "100%" }} initialValue={question.fieldType} name={`fieldType_${option.id}_${question.id}`} label="Type" rules={[{ required: true, message: "Field type is required" }]}>
                    <Select
                      size="large"
                      options={Object.entries(SubQuestionTypes).map(([key, value]) => ({
                        label: key,
                        value: value,
                      }))}
                      placeholder="Select field type"
                      value={question.fieldType}
                      onChange={(value) => handleChange(option.id, question.id, "fieldType", value)}
                    />
                  </Form.Item>

                  <Form.Item style={{ width: "100%" }} name={`isRequired_${option.id}_${question.id}`} valuePropName="checked" initialValue={question.isRequired}>
                    <Checkbox checked={question.isRequired} onChange={(e) => handleChange(option.id, question.id, "isRequired", e.target.checked)}>
                      Is Required
                    </Checkbox>
                  </Form.Item>
                </StyledConditionalContainer>
              ))}
              <AddAnotherConditionalStyledButton onClick={() => addQuestion(option.id)}>
                <PlusOutlined /> Add Another Conditional Question
              </AddAnotherConditionalStyledButton>
            </div>
          )}
        </StyledOptionContainer>
      ))}
      <Button type="dashed" onClick={addOption} icon={<PlusOutlined />} style={{ width: "100%" }}>
        Add Option
      </Button>
    </Form.Item>
  );
};

export default DynamicOptionForm;
